import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 transition-opacity duration-300",
      "pointer-events-none",
      className
    )}
    style={{ willChange: 'opacity' }}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * DialogContent - Standard dialog for standalone use
 * 
 * Z-index: 50 (content + overlay)
 * 
 * @example
 * ```tsx
 * <Dialog open={open} onOpenChange={setOpen}>
 *   <DialogContent>
 *     <DialogTitle>My Dialog</DialogTitle>
 *   </DialogContent>
 * </Dialog>
 * ```
 */
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-fit w-[calc(100%-1.5rem)] sm:w-auto sm:max-w-2xl translate-x-[-50%] translate-y-[-50%] gap-3 sm:gap-4 bg-background/95 backdrop-blur-md p-4 sm:p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg touch-manipulation max-h-[95vh] sm:max-h-[90vh] overflow-y-auto overscroll-contain",
        "pointer-events-auto",
        className
      )}
      style={{ 
        willChange: 'transform, opacity',
        contain: 'layout style paint',
        touchAction: 'pan-y'
      }}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

/**
 * MobileDialogContent - Responsive centered dialog with mobile-friendly sizing
 * 
 * Z-index: 60 (content + overlay)
 * 
 * @example
 * ```tsx
 * // Standard responsive dialog
 * <Dialog open={open} onOpenChange={setOpen}>
 *   <MobileDialogContent>
 *     <DialogTitle>Responsive Dialog</DialogTitle>
 *   </MobileDialogContent>
 * </Dialog>
 * ```
 */
const MobileDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Mobile: Fullscreen positioning
        "fixed inset-0 z-[60]",
        "w-screen h-[100svh] max-h-[100svh] min-h-[100svh]",
        // Desktop: Centered positioning with natural sizing
        "md:inset-auto md:left-[50%] md:top-[50%]",
        "md:translate-x-[-50%] md:translate-y-[-50%]",
        "md:w-[calc(100vw-4rem)] md:max-w-6xl",
        "md:h-auto md:max-h-[85vh] md:min-h-0",
        "md:rounded-lg md:border md:border-border",
        // Flexible layout
        "flex flex-col",
        // Padding
        "p-4 md:p-6",
        // Styling
        "bg-background md:shadow-lg",
        // Scrolling support
        "overflow-y-auto md:overflow-visible",
        // Animations (fade for mobile, zoom/slide for desktop)
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "md:data-[state=closed]:zoom-out-95 md:data-[state=open]:zoom-in-95",
        "md:data-[state=closed]:slide-out-to-left-1/2 md:data-[state=closed]:slide-out-to-top-[48%]",
        "md:data-[state=open]:slide-in-from-left-1/2 md:data-[state=open]:slide-in-from-top-[48%]",
        "duration-200",
        className
      )}
      style={{ 
        willChange: 'transform, opacity',
        contain: 'layout style paint',
        pointerEvents: 'auto',
        touchAction: 'pan-y',
        WebkitOverflowScrolling: 'touch'
      }}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
))
MobileDialogContent.displayName = "MobileDialogContent"

/**
 * FullscreenDialogContent - True fullscreen on both mobile and desktop
 * 
 * Z-index: 60 (content + overlay)
 * Use this when you need edge-to-edge fullscreen experience
 * 
 * @example
 * ```tsx
 * <Dialog open={open} onOpenChange={setOpen}>
 *   <FullscreenDialogContent>
 *     <DialogTitle>Fullscreen Experience</DialogTitle>
 *   </FullscreenDialogContent>
 * </Dialog>
 * ```
 */
const FullscreenDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Fullscreen positioning on all breakpoints
        "fixed inset-0 z-[60]",
        // Flex layout for header/content/footer structure
        "flex flex-col",
        // Full dimensions
        "w-screen h-[100svh] max-h-[100svh]",
        // Styling
        "bg-background",
        // Simple fade animation (no slide/zoom for fullscreen)
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "duration-200",
        "pointer-events-auto",
        className
      )}
      style={{ 
        height: '100svh',
        maxHeight: '100svh',
        minHeight: '100svh',
        willChange: 'opacity',
        contain: 'layout style paint'
      }}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
))
FullscreenDialogContent.displayName = "FullscreenDialogContent"

/**
 * NestedDialogContent - Use when opening a dialog from another dialog
 * 
 * Z-index: 70 (content) / 60 (overlay)
 * 
 * @example
 * ```tsx
 * // Parent dialog uses MobileDialogContent (z-60)
 * // Child dialog uses NestedDialogContent (z-70)
 * <Dialog open={showNested} onOpenChange={setShowNested}>
 *   <NestedDialogContent>
 *     <DialogTitle>Nested Dialog</DialogTitle>
 *   </NestedDialogContent>
 * </Dialog>
 * ```
 */
const NestedDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogPrimitive.Overlay
      className={cn(
        "fixed inset-0 z-[65] backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 transition-opacity duration-300",
      )}
      style={{ willChange: 'opacity' }}
    />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-[70] grid w-full max-w-[calc(100%-1.5rem)] sm:w-full sm:max-w-6xl translate-x-[-50%] translate-y-[-50%] gap-3 sm:gap-4 bg-background/95 backdrop-blur-md p-4 sm:p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg touch-manipulation max-h-[95vh] sm:max-h-[90vh] overflow-y-auto overscroll-contain",
        "pointer-events-auto",
        className
      )}
      style={{ 
        willChange: 'transform, opacity',
        contain: 'layout style paint'
      }}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
))
NestedDialogContent.displayName = "NestedDialogContent"

/**
 * DeepNestedDialogContent - Use for 3+ levels of dialog nesting (rare)
 * 
 * Z-index: 90 (content) / 80 (overlay)
 * 
 * @example
 * ```tsx
 * // Level 1: Base dialog (z-50)
 * // Level 2: Nested dialog (z-70)
 * // Level 3: Deep nested dialog (z-90)
 * <Dialog open={showDeepNested} onOpenChange={setShowDeepNested}>
 *   <DeepNestedDialogContent>
 *     <DialogTitle>Deep Nested Dialog</DialogTitle>
 *   </DeepNestedDialogContent>
 * </Dialog>
 * ```
 */
const DeepNestedDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogPrimitive.Overlay
      className={cn(
        "fixed inset-0 z-[80] backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 transition-opacity duration-300",
      )}
      style={{ willChange: 'opacity' }}
    />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-[90] grid w-full max-w-[calc(100%-1.5rem)] sm:w-full sm:max-w-6xl translate-x-[-50%] translate-y-[-50%] gap-3 sm:gap-4 bg-background/95 backdrop-blur-md p-4 sm:p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg touch-manipulation max-h-[95vh] sm:max-h-[90vh] overflow-y-auto overscroll-contain",
        "pointer-events-auto",
        className
      )}
      style={{ 
        willChange: 'transform, opacity',
        contain: 'layout style paint'
      }}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DeepNestedDialogContent.displayName = "DeepNestedDialogContent"

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  MobileDialogContent,
  FullscreenDialogContent,
  NestedDialogContent,
  DeepNestedDialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
